import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CacheTags,
  ClientAdminRequest,
  PaginatedRequest,
  SomeRequest,
} from 'types';
import { InvoiceSearchRecord } from './bindings/InvoiceSearchRecord';
import { InvoiceResponse } from './bindings/InvoiceResponse';
import { VendorResponse } from './bindings/VendorResponse';
import { FetchResponse } from './bindings/FetchResponse';
import { OdinBaseQuery } from './core';
import { PostInvoiceRequest } from './bindings/PostInvoiceRequest';
import { PutInvoiceRequest } from './bindings/PutInvoiceRequest';
import { InvoiceSearchRecordResponse } from './bindings/InvoiceSearchRecordResponse';
import { PostVendorRequest } from './bindings/PostVendorRequest';
import { PutVendorRequest } from './bindings/PutVendorRequest';

export const invoicesApi = createApi({
  reducerPath: 'invoices',
  baseQuery: OdinBaseQuery,
  tagTypes: [CacheTags.vendors, CacheTags.invoices],
  endpoints: (builder) => ({
    // fetchClientVendors: builder.query<VendorResponse[], ClientAdminRequest>({
    //   query: (payload) => ({
    //     url: '/api/vendors',
    //     method: 'GET',
    //     params: payload,
    //   }),
    // }),
    fetchSomeVendors: builder.query<VendorResponse[], SomeRequest>({
      query: (payload) => ({
        url: '/api/vendors',
        method: 'GET',
        params: payload,
      }),
      providesTags: (payload) => {
        let returnValue: { type: string; id: string }[] = [];

        if (payload) {
          returnValue = [
            ...payload.map((v) => ({
              type: CacheTags.vendors,
              id: v.id,
            })),
          ];
        }

        return returnValue;
      },
    }),
    fetchVendorsPage: builder.query<
      FetchResponse<VendorResponse>,
      PaginatedRequest
    >({
      query: (payload) => ({
        url: '/api/vendors',
        method: 'GET',
        params: payload,
      }),
      providesTags: (payload) => {
        let returnValue = [{ type: CacheTags.vendors, id: CacheTags.partial }];

        if (payload) {
          returnValue = [
            ...returnValue,
            ...(payload?.items.map(({ id }) => ({
              type: CacheTags.vendors,
              id: id,
            })) ?? []),
          ];
        }

        return returnValue;
      },
    }),
    fetchInvoiceDetail: builder.query<InvoiceResponse, string>({
      query: (payload) => ({
        url: `/api/invoices/${payload}`,
        method: 'GET',
      }),
      // transformResponse: (response: InvoiceView) => {
      //   return augmentInvoiceDetail(response);
      // },
      providesTags: (payload) => [
        { type: CacheTags.invoices, id: payload?.id },
        { type: CacheTags.invoiceDetail, id: payload?.id },
      ],
    }),
    fetchSomeInvoices: builder.query<
      FetchResponse<InvoiceSearchRecord>,
      SomeRequest
    >({
      query: (payload) => ({
        url: '/api/invoices',
        method: 'GET',
        params: payload,
      }),
      providesTags: (payload) => [
        ...(payload?.items?.map((i) => ({
          type: CacheTags.invoices,
          id: i.id,
        })) || []),
      ],
    }),
    fetchInvoicesPage: builder.query<
      FetchResponse<InvoiceSearchRecordResponse>,
      PaginatedRequest
    >({
      query: (payload) => ({
        url: '/api/invoices',
        method: 'GET',
        params: payload,
      }),
      providesTags: (payload) => {
        let returnValue = [{ type: CacheTags.invoices, id: CacheTags.partial }];

        if (payload) {
          returnValue = [
            ...returnValue,
            ...payload.items.map(({ id }) => ({
              type: CacheTags.invoices,
              id: id,
            })),
          ];
        }

        return returnValue;
      },
    }),
    postVendor: builder.mutation<VendorResponse, PostVendorRequest>({
      query: (payload) => ({
        url: `/api/vendors`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, _, payload) => [
        { type: CacheTags.vendorDetail, id: CacheTags.partial },
        { type: CacheTags.vendors, id: CacheTags.partial },
      ],
    }),
    putVendor: builder.mutation<
      VendorResponse,
      { id: string } & PutVendorRequest
    >({
      query: (payload) => ({
        url: `/api/vendors/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, _, payload) => [
        { type: CacheTags.vendorDetail, id: payload.id },
        { type: CacheTags.vendors, id: payload.id },
      ],
    }),
    // saveVendor: builder.mutation<VendorResponse, VendorResponse>({
    //   query: (payload) => ({
    //     url: `/api/vendors${
    //       !!BigInt(payload.id || '0') ? '/' + payload.id : ''
    //     }`,
    //     method: !!BigInt(payload.id || '0') ? 'PUT' : 'POST',
    //     body: payload,
    //   }),
    //   invalidatesTags: (result) => [
    //     { type: CacheTags.vendorDetail, id: result?.id },
    //     { type: CacheTags.vendors, id: result?.id },
    //   ],
    // }),
    postInvoice: builder.mutation<InvoiceResponse, PostInvoiceRequest>({
      query: (payload) => ({
        url: '/api/invoices',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result) => [
        { type: CacheTags.invoices, id: CacheTags.partial },
        { type: CacheTags.invoiceDetail, id: result?.id },
      ],
    }),
    putInvoice: builder.mutation<
      InvoiceResponse,
      { id: string } & PutInvoiceRequest
    >({
      query: (payload) => ({
        url: `/api/invoices/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: CacheTags.invoices, id: CacheTags.partial },
        { type: CacheTags.invoiceDetail, id: payload.id },
      ],
    }),
  }),
});

export const {
  useFetchVendorsPageQuery,
  useFetchInvoiceDetailQuery,
  useFetchSomeInvoicesQuery,
  useFetchInvoicesPageQuery,
  // useSaveVendorMutation,
  useFetchSomeVendorsQuery,
  usePostInvoiceMutation,
  usePutInvoiceMutation,
  // useFetchClientVendorsQuery,
  usePostVendorMutation,
  usePutVendorMutation,
} = invoicesApi;

// export function augmentInvoiceDetail(i: InvoiceView): InvoiceView {
//   i.transactions = i.transactions.map((t) => {
//     t.amount = t.accounts.reduce((sum, n) => {
//       sum = new Decimal(sum).add(n.amount).toString();
//       return sum;
//     }, '0');
//     return t;
//   });
//   return i;
// }
